/**
 * **Die Silhouette der Aufgaben-Details** — die Karte und der Buckel der
 * Zeichen-Leiste als EIN geschlossener Pfad.
 *
 * Dieselbe Idee wie die Reiter-Form des Fensterrahmens (`unionPath.ts`): Was
 * zusammengehört, bekommt eine gemeinsame Außenlinie. Der Unterschied ist die
 * Achse — dort sitzt der Buckel OBEN, hier LINKS.
 *
 * **Die Zahlen stammen aus dem Entwurf, nicht aus einer Näherung.** Figma
 * „2-Master", Knoten `5279:208021` („Union" im Drawer `5279:208009`): die
 * Vereinigung aus dem Kartenrechteck (x 31, 620 × 726) und einem abgerundeten
 * Rechteck 48 × 396 bei x 0. Der Pfad dort ist ausgemessen und hier
 * nachgebaut — samt Figmas **weichen Ecken**, die keine Viertelkreise sind,
 * sondern drei Bezier-Segmente mit einer festen Kurvenfolge. Ein Viertelkreis
 * an ihrer Stelle sieht am 38-px-Radius des Buckels sichtbar anders aus.
 *
 * `buildUnionPath` taugte dafür nicht: Seine S-Kurve ist `CURVE_WIDTH = 48`
 * breit, der Buckel steht aber nur 31 px vor — sie hätte keinen Platz. Die
 * beiden ausgelieferten Aufrufer dort (Fensterrahmen, `AreaNode`) bleiben
 * deshalb unberührt.
 */

/** Radius der Kartenecken (Figma: 16). */
export const CARD_CORNER_RADIUS = 16
/** Wie weit der Buckel über die Kartenkante nach LINKS hinaussteht. */
export const BUMP_DEPTH = 31
/**
 * Die volle Breite einer Pille — und des Buckel-Rechtecks im Entwurf.
 * Was über {@link BUMP_DEPTH} hinausgeht, verschluckt die Karte.
 */
export const PILL_WIDTH = 48
/** Wie weit die Pillen von der äußeren Kante des Buckels einrücken (Figma: x = 8). */
export const PILL_INSET = 8
/** Eckradius des Buckels. Figmas weiche Ecke, gemessen am Pfad. */
export const BUMP_CORNER_RADIUS = 38.4004
/**
 * Wie weit UNTER dem Buckel die Kartenkante nach links abbiegt.
 *
 * Der Übergang ist keine Hohlkehle mit eigenem Radius, sondern ein einzelnes
 * Bezier-Segment, das tangential aus der senkrechten Kartenkante herausläuft
 * und tangential in die weiche Ecke des Buckels übergeht.
 */
export const BUMP_JOIN_RUN = 18.213

/** Radius der Pillen-Ecken (Figma: `rounded-2xl`). */
export const PILL_RADIUS = 16
/** Abstand zwischen zwei Pillen. */
export const PILL_GAP = 8
/** Luft über der ersten Pille. */
export const RAIL_TOP = 8
/** Wie weit der Buckel unter die letzte Pille reicht. */
export const RAIL_BOTTOM = 12
/** Kantenlänge eines Sprungziels. */
export const TARGET_SIZE = 32
/** Abstand zwischen zwei Zielen innerhalb einer Pille. */
export const TARGET_GAP = 4
/** Polster einer Pille rings um ihre Ziele. */
export const PILL_PADDING = 8

/**
 * Figmas **weiche Ecke**, auf Radius 1 normiert: zehn Punkte (vier auf der
 * Linie, sechs Kontrollpunkte) als Paare `[längs, quer]`. Längs zählt vom
 * Eckpunkt zurück in die ankommende Richtung, quer weiter in die abgehende.
 *
 * Aus `5279:208021` abgelesen und durch den jeweiligen Radius geteilt; beide
 * Ecken dort (Karte 16, Buckel 38.4) liefern dieselbe Folge.
 */
const SMOOTH_CORNER: readonly (readonly [number, number])[] = [
  [1, 0], [0.649938, 0], [0.474938, 0],
  [0.341250, 0.068115], [0.223625, 0.128036], [0.128063, 0.223650],
  [0.068125, 0.341248], [0, 0.474943], [0, 0.649969], [0, 1],
]

/** Drei Nachkommastellen reichen und halten den Pfad lesbar. */
const f = (n: number) => Math.round(n * 1000) / 1000

/**
 * Eine weiche Ecke am Punkt `(cx, cy)`: Wir kommen in Richtung `u` an und
 * gehen in Richtung `v` weiter. Liefert die drei `C`-Segmente; der Startpunkt
 * (Eckpunkt minus `u * r`) muss vom Aufrufer angefahren sein.
 */
function smoothCorner(
  cx: number, cy: number,
  ux: number, uy: number,
  vx: number, vy: number,
  r: number,
): string[] {
  const p = SMOOTH_CORNER.map(([along, across]) => [
    cx - ux * along * r + vx * across * r,
    cy - uy * along * r + vy * across * r,
  ] as const)
  const seg = (i: number) =>
    `C ${f(p[i][0])},${f(p[i][1])} ${f(p[i + 1][0])},${f(p[i + 1][1])} ${f(p[i + 2][0])},${f(p[i + 2][1])}`
  return [seg(1), seg(4), seg(7)]
}

/** Ein Buckel an der linken Kartenkante — von `top` bis `top + height`. */
export interface Bump {
  top: number
  height: number
}

/** Die Höhe einer Pille mit `count` Zielen. 2 → 84, 3 → 120, 4 → 156. */
export function pillHeight(count: number): number {
  if (count <= 0) return 0
  return 2 * PILL_PADDING + count * TARGET_SIZE + (count - 1) * TARGET_GAP
}

/**
 * Wo die Pillen liegen, wenn die Gruppen `counts` Ziele tragen. Leere Gruppen
 * fallen weg — bei mehreren ausgewählten Aufgaben ist das Gruppe 1.
 */
export function railBumps(counts: readonly number[]): Bump[] {
  const bumps: Bump[] = []
  let top = RAIL_TOP
  for (const count of counts) {
    const height = pillHeight(count)
    if (height === 0) continue
    bumps.push({ top, height })
    top += height + PILL_GAP
  }
  return bumps
}

/**
 * Wie weit der Buckel von der Oberkante der Karte nach unten reicht.
 * Eine Aufgabe (2/4/3) → 396, mehrere (4/3) → 304 — die Zahlen des Entwurfs.
 */
export function railExtent(bumps: readonly Bump[]): number {
  const last = bumps[bumps.length - 1]
  return last ? last.top + last.height + RAIL_BOTTOM : 0
}

/** Nur die Karte, ohne Buckel — für zu kleine Felder und den Leerzustand vor der ersten Messung. */
function cardOnly(w: number, h: number): string {
  const R = CARD_CORNER_RADIUS
  const D = BUMP_DEPTH
  return [
    `M ${D + R},0`,
    `L ${w - R},0`,
    ...smoothCorner(w, 0, 1, 0, 0, 1, R),
    `L ${w},${h - R}`,
    ...smoothCorner(w, h, 0, 1, -1, 0, R),
    `L ${D + R},${h}`,
    ...smoothCorner(D, h, -1, 0, 0, -1, R),
    `L ${D},${R}`,
    ...smoothCorner(D, 0, 0, -1, 1, 0, R),
    'Z',
  ].join(' ')
}

/**
 * Der geschlossene Pfad aus Karte und Buckel.
 *
 * Der Kasten liegt bei `x ∈ [BUMP_DEPTH, w]`, der Buckel steht links davor bis
 * `x = 0` und reicht von der Oberkante bis `bumpBottom`. `w` und `h` sind die
 * AUSSENMASSE des gezeichneten Feldes, Leiste eingeschlossen.
 *
 * Passt der Buckel nicht (zu kurzes Feld), bleibt die Karte für sich — lieber
 * eine glatte Kante als eine Schlaufe.
 */
export function buildSideUnionPath(w: number, h: number, bumpBottom: number): string {
  const R = CARD_CORNER_RADIUS
  const D = BUMP_DEPTH
  const BR = BUMP_CORNER_RADIUS

  if (w - D <= 2 * R || h <= 2 * R) return ''
  // Oben braucht der Buckel seine Ecke, unten Ecke plus Auslauf — und darunter
  // muss noch die Kartenecke Platz haben.
  if (bumpBottom < 2 * BR || bumpBottom + BUMP_JOIN_RUN > h - R) return cardOnly(w, h)

  const B = bumpBottom
  return [
    `M ${BR},0`,
    `L ${w - R},0`,
    ...smoothCorner(w, 0, 1, 0, 0, 1, R),
    `L ${w},${h - R}`,
    ...smoothCorner(w, h, 0, 1, -1, 0, R),
    `L ${D + R},${h}`,
    ...smoothCorner(D, h, -1, 0, 0, -1, R),
    // Die Kartenkante hoch bis dorthin, wo sie zum Buckel abbiegt.
    `L ${D},${f(B + BUMP_JOIN_RUN)}`,
    /*
      **Der Übergang ist kein eigener Radius.** Er ist das ERSTE Segment der
      weichen Buckelecke, ersetzt durch eines, das weiter unten an der
      senkrechten Kartenkante ansetzt: tangential dort (beide Punkte auf
      `x = D`) und tangential am Einlauf in die Ecke. Die beiden übrigen
      Segmente der Ecke folgen unverändert — deshalb `slice(1)`.
    */
    `C ${D},${f(B + 8.112)} ${f(D - 8.895)},${f(B + 1.97)} ${f(BR * 0.34125)},${f(B - BR * 0.068115)}`,
    ...smoothCorner(0, B, -1, 0, 0, -1, BR).slice(1),
    `L 0,${f(BR)}`,
    ...smoothCorner(0, 0, 0, -1, 1, 0, BR),
    `L ${BR},0`,
    'Z',
  ].join(' ')
}
